// Sub-phase U.2b — theme management.
//
// Three settings:
//
//   - 'auto'  : follow OS preference (prefers-color-scheme). Default.
//   - 'light' : force light.
//   - 'dark'  : force dark.
//
// Shoelace's dark theme is applied via the `sl-theme-dark` class on
// <html>. Both shoelace/themes/{light,dark}.css must be loaded — the
// switch is done by toggling the class, not by swapping stylesheets.
// (index.html loads both unconditionally so the switch is instant.)
//
// Persistence: a single string in localStorage under "easypos.theme".
// Missing / unrecognised values fall back to 'auto'.

export type ThemeChoice = 'auto' | 'light' | 'dark';

const STORAGE_KEY = 'easypos.theme';

type Listener = (effective: 'light' | 'dark', choice: ThemeChoice) => void;

class ThemeStore {
  private _choice: ThemeChoice = 'auto';
  private _listeners = new Set<Listener>();
  private _mediaQuery: MediaQueryList | null = null;

  init(): void {
    this._choice = readStored();
    this._mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this._mediaQuery.addEventListener('change', () => this._apply());
    this._apply();
  }

  get choice(): ThemeChoice {
    return this._choice;
  }

  get effective(): 'light' | 'dark' {
    if (this._choice === 'dark') return 'dark';
    if (this._choice === 'light') return 'light';
    return this._mediaQuery?.matches ? 'dark' : 'light';
  }

  setChoice(next: ThemeChoice): void {
    this._choice = next;
    localStorage.setItem(STORAGE_KEY, next);
    this._apply();
  }

  cycle(): void {
    // auto → light → dark → auto
    const next: ThemeChoice =
      this._choice === 'auto'
        ? 'light'
        : this._choice === 'light'
          ? 'dark'
          : 'auto';
    this.setChoice(next);
  }

  subscribe(listener: Listener): () => void {
    this._listeners.add(listener);
    listener(this.effective, this._choice);
    return () => this._listeners.delete(listener);
  }

  private _apply(): void {
    const effective = this.effective;
    if (effective === 'dark') {
      document.documentElement.classList.add('sl-theme-dark');
    } else {
      document.documentElement.classList.remove('sl-theme-dark');
    }
    for (const l of this._listeners) {
      try {
        l(effective, this._choice);
      } catch (err) {
        console.error('[theme] listener threw', err);
      }
    }
  }
}

function readStored(): ThemeChoice {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'auto') return raw;
  return 'auto';
}

export const themeStore = new ThemeStore();
